/**
 * What the server told us about itself (shared/contract.ts): its auth mode,
 * its wire contract, the oldest client contract it still serves, and its
 * version. Filled by `fetchAuthMode()` at app start and by any 426 the API
 * answers with. The embedded shell reads `clientOutdated` to replace the app
 * with an "update Rainvic Tool" screen instead of misreading a newer server;
 * the version badge reads `serverVersion` to show client/server skew.
 */
import { KOLA_API_CONTRACT, type AuthModeResponse, type KolaAuthMode } from "@kola/shared";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

export interface ServerInfo {
  mode: KolaAuthMode | null;
  serverContract: number | null;
  minClientContract: number | null;
  serverVersion: string | null;
  /** This build's contract is below the server's floor — stop and update. */
  clientOutdated: boolean;
}

export const serverInfoStore = createStore<ServerInfo>(() => ({
  mode: null,
  serverContract: null,
  minClientContract: null,
  serverVersion: null,
  clientOutdated: false,
}));

export function noteAuthMode(info: AuthModeResponse): void {
  serverInfoStore.setState({
    mode: info.mode,
    serverContract: info.contract,
    minClientContract: info.minClientContract,
    serverVersion: info.version,
    clientOutdated: KOLA_API_CONTRACT < info.minClientContract,
  });
}

/** A 426 `client-outdated` body — the floor moved while this tab was open. */
export function noteClientOutdated(body: unknown): void {
  const record = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const current = serverInfoStore.getState();
  serverInfoStore.setState({
    clientOutdated: true,
    minClientContract: num(record["minClientContract"]) ?? current.minClientContract,
    serverContract: num(record["contract"]) ?? current.serverContract,
  });
}

export function useServerInfo(): ServerInfo {
  return useStore(serverInfoStore);
}
